import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Alert, FormHelperText, Divider } from '@mui/material';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import { setSiteDetailsData } from '../../../redux/siteDetailsDataSlice';
import { CustomSingleSelect } from '../../../utils/CustomSingleSelect';
import { CustomMultiSelect } from '../../../utils/CustomMultiSelect';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { useCallback } from 'react';
import { useFormik } from 'formik';
import Iconify from '../../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../../css/sites/addSiteRelation.css';

export const EditSiteRelation = ({ editRelation, setEditRelation, site_id }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const siteDetailsData = useSelector((state) => state.SiteDetailsData);

    const [errMsg, setErrMsg] = React.useState('');

    const formik = useFormik({
        initialValues: {
            pole: { value: editRelation?.relationData?.pole_id, label: editRelation?.relationData?.pole_name },
            metiers: editRelation?.relationData?.metier?.map(metier => ({ value: metier?.metier_id, label: metier?.metier_name })),
        },
        validationSchema: Yup.object().shape({
            pole: Yup.object().required(t('isRequired')),
            metiers: Yup.array().min(1, t('isRequired')),
        }),
        onSubmit: async (values) => {
            try {
                const data = {
                    new_pole_id: values?.pole?.value,
                    metiers: values?.metiers?.map(metier => metier.value),
                };

                const response = await axios.patch(`site-pole-metier/${site_id}/${editRelation.relationData.pole_id}`, data);
                if (response.status === 200 || response.status === 201) {
                    // Transform the new relation data to match the desired format
                    const updatedRelations = values?.metiers?.map(metier => ({
                        id: crypto.randomUUID(), // Generate a unique ID for the new relation
                        metier: {
                            id: metier?.value,
                            name: metier?.label,
                        },
                        pole: {
                            id: values?.pole?.value,
                            name: values?.pole?.label,
                        },
                    }));

                    // Update the site_pole_metier array in siteDetailsData
                    const updatedSitePoleMetier = siteDetailsData?.site_pole_metier
                        .filter(relation => relation.pole.id !== editRelation?.relationData?.pole_id) // Remove old relations for the pole
                        .concat(updatedRelations); // Add the updated relations

                    // Dispatch the updated siteDetailsData
                    dispatch(setSiteDetailsData({
                        ...siteDetailsData,
                        site_pole_metier: updatedSitePoleMetier,
                    }));

                    // Close the edit relation dialog
                    dispatch(setSuccessfullModifyDialog({ open: true, type: 'Modifier la relation de site' }));
                    setEditRelation({ relationData: null, open: false });
                }
            } catch (error) {
                setErrMsg(error.message);
                console.error('Error updating relation:', error);
                CustomErrorLogger(error, "error updating relation in EditSiteRelation");
            }
        }
    });

    const { handleSubmit, values, touched, errors, setFieldValue } = formik;

    const fetchPoles = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('pole/list', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response?.data?.poles?.map(pole => ({
                    value: pole.id,
                    label: pole.name,
                }));
            }
        } catch (error) {
            console.error('Error fetching poles:', error);
            CustomErrorLogger(error, "error fetching poles in AddSiteRelation");
        }
    }, []);

    const fetchMetiers = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('metier/list', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response?.data?.metiers?.map(metier => ({
                    value: metier.id,
                    label: metier.name,
                }));
            }
        } catch (error) {
            console.error('Error fetching metiers:', error);
            CustomErrorLogger(error, "error fetching metiers in AddSiteRelation");
        }
    }, []);

    const handleClose = useCallback(() => {
        setEditRelation({ relationData: null, open: false });
    }, [setEditRelation]);

    return (
        <Dialog open={editRelation.open} fullWidth maxWidth='md'>
            <IconButton onClick={handleClose} className='addSiteRelation_icon_button'>
                <Iconify icon={"material-symbols:close"} className='addSiteRelation_close_icon' />
            </IconButton>
            <DialogTitle className='addSiteRelation_dialog_title'>Modifier le pôle et les métiers associées</DialogTitle>
            <Divider />
            {errMsg && <Alert key={errMsg} severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addSiteRelation_dialog_content'>
                    <Box className='addSiteRelation_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addSiteRelationInputTitle">{t('Pôle')}</FormHelperText>
                        <CustomSingleSelect
                            placeholder='Sélectionnez un pôle'
                            className='addSiteRelation_InputField'
                            required={true}
                            value={values.pole}
                            fetchFunction={fetchPoles}
                            backendSearchable={true}
                            onChange={(value) => setFieldValue('pole', value)}
                            helperText={touched.pole && errors.pole}
                            error={touched.pole && Boolean(errors.pole)}
                        />
                    </Box>
                    <Box className='addSiteRelation_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addSiteRelationInputTitle">{t('Metier')}</FormHelperText>
                        <CustomMultiSelect
                            placeholder='Sélectionnez des métiers'
                            className='addSiteRelation_InputField'
                            required={true}
                            value={values.metiers}
                            fetchFunction={fetchMetiers}
                            backendSearchable={true}
                            onChange={(value) => setFieldValue('metiers', value)}
                            helperText={touched.metiers && errors.metiers}
                            error={touched.metiers && Boolean(errors.metiers)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions className='addSiteRelation_dialog_actions'>
                    <Button className='addSiteRelation_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addSiteRelation_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};